import type { Clip } from "./clip.js";
import { mergeOverlays } from "./overlays.js";

/** Field groups that background jobs own; each has a stamp in clip.stamps. */
export const JOB_GROUPS = {
  meta: ["meta"],
  versions: ["versions"],
  cover: ["coverMs", "coverPick", "coverCandidates"],
  analysis: ["silences", "facePoints", "flags"],
  quran: ["quranSpans", "quranDetector"],
  direction: ["direction"],
} as const;
export type JobGroup = keyof typeof JOB_GROUPS;

/** Merges a clip saved by an editor (or any older copy) onto the latest saved clip: whichever side has the newer stamp keeps its job-owned fields, and the longer history wins. */
export function mergeJobFields(latest: Clip, incoming: Clip): Clip {
  let out: Clip = { ...incoming, stamps: { ...incoming.stamps } };
  for (const g of Object.keys(JOB_GROUPS) as JobGroup[]) {
    const mine = latest.stamps[g], theirs = incoming.stamps[g];
    if (mine && (!theirs || mine > theirs)) {
      for (const f of JOB_GROUPS[g]) (out as Record<string, unknown>)[f] = (latest as Record<string, unknown>)[f];
      out.stamps[g] = mine;
    }
  }
  // overlays are shared: the owner edits them while jobs add images and the planner adds new ones
  if (latest.stamps.overlays && latest.stamps.overlays !== incoming.stamps.overlays) {
    out = { ...out, overlays: mergeOverlays(latest.overlays, incoming.overlays, incoming.stamps.overlays), stamps: { ...out.stamps, overlays: latest.stamps.overlays } };
  }
  if (latest.history.length > incoming.history.length) out = { ...out, history: latest.history };
  return out;
}

/** Marks a job group as freshly written. */
export const stamp = (clip: Clip, g: JobGroup | "overlays"): Clip => ({ ...clip, stamps: { ...clip.stamps, [g]: new Date().toISOString() } });
